import { IconButton } from '@chakra-ui/react';
import { MdClose } from 'react-icons/md';
import { Tooltip } from '@/components/v4';
import NumberCellRenderer from '../../edit/cellRenderer/NumberCellRenderer';
import StatusCellRenderer from '../../edit/cellRenderer/StatusCellRenderer';
import { VIEW_MODES } from '../constants';
import {
    ATTRIBUTE_FILTERS,
    FILTER_COLUMNS,
    NUMBER_FILTER_OPTIONS,
    OVERSTAMP_DIFFERS_VALUE,
    compareFilterValues,
    getSetFilterValue,
    getValueLabel,
    isOverstampDifferent,
} from '../utils/filters';
import {
    FilterValueWithCount,
    TextCell,
    TrunkCell,
    UserCell,
} from './BulkCells';

const centredCell = { display: 'flex', alignItems: 'center' };

export const getRowId = (params) => params.data.id;

/**
 * Why a greyed out number can't be ticked, for the tooltip shown anywhere on
 * its row in All Numbers (see RowHoverTooltip).
 */
export const getUnselectableLabel = (data) =>
    data?.unselectableReason
        ? `Can't be selected: ${data.unselectableReason}`
        : null;

const makeNumberCol = (countries, headerName = 'Number') => ({
    colId: 'number',
    field: 'e164',
    headerName,
    minWidth: 190,
    flex: 1.2,
    cellRenderer: (params) => (
        <NumberCellRenderer
            value={params.data.telephoneNumber}
            valueFormatted={params.data.e164}
            countries={countries}
            params={params}
        />
    ),
});

const makeStatusCol = () => ({
    colId: 'status',
    field: 'statusLabel',
    headerName: 'Status',
    width: 175,
    minWidth: 175,
    flex: 0,
    cellRenderer: ({ data }) => (
        <StatusCellRenderer
            value={data.statusKey}
            statusMessage={data.statusMessage}
            operatorStatus={data.operatorStatus}
            operatorMessage={data.operatorMessage}
        />
    ),
    cellStyle: centredCell,
});

const makeTrunkCol = () => ({
    colId: 'trunk',
    field: 'trunkName',
    headerName: 'Trunk',
    minWidth: 200,
    flex: 1,
    cellRenderer: TrunkCell,
    cellStyle: centredCell,
});

const makeTextCol = (colId, field, headerName) => ({
    colId,
    field,
    headerName,
    minWidth: 150,
    flex: 1,
    cellRenderer: TextCell,
    cellStyle: centredCell,
});

const makeCountryCol = () => makeTextCol('country', 'countryName', 'Country');

// Narrow: an icon when a Teams user has the number, no name
const makeUserCol = () => ({
    colId: 'user',
    field: 'userAssigned',
    headerName: 'User',
    width: 96,
    minWidth: 96,
    flex: 0,
    cellRenderer: UserCell,
    cellStyle: { ...centredCell, justifyContent: 'center' },
});

const makeCallingProfileCol = () =>
    makeTextCol('callingProfile', 'callingProfileName', 'Calling Profile');

const makeUsageCol = () => makeTextCol('usage', 'usageName', 'Usage Type');

const makeRemoveCol = (onRemove) => ({
    colId: 'remove',
    headerName: '',
    // Always there, and left out of Choose Columns
    lockVisible: true,
    suppressColumnsToolPanel: true,
    width: 64,
    minWidth: 64,
    flex: 0,
    resizable: false,
    cellRenderer: ({ data }) => (
        // On the left: on the right it runs off the page and adds scrollbars
        <Tooltip label="Remove from selection" hasArrow placement="left">
            <IconButton
                aria-label="Remove from selection"
                icon={<MdClose />}
                size="sm"
                variant="ghost"
                onClick={() => onRemove(data.id)}
            />
        </Tooltip>
    ),
    cellStyle: centredCell,
});

// Trunk and Operator Connect details only fit when a list has the full width.
const getDetailCols = (tenant) => [
    makeTrunkCol(),
    ...(tenant.oc ? [makeCallingProfileCol(), makeUsageCol()] : []),
];

// All Numbers columns shown only in Full view. Split View keeps them in the
// grid, hidden, so they can still be shown from the header's right-click
// menu (Choose Columns).
export const FULL_VIEW_COLUMNS = [
    'country',
    'trunk',
    'callingProfile',
    'usage',
];

// Native AG Grid filters for the All Numbers list. The toolbar reads and
// writes the same filter model, so header filters and toolbar stay in sync.
const attributeFilter = (key) => {
    const { field, order } = ATTRIBUTE_FILTERS.find(
        (filter) => filter.key === key,
    );
    return {
        filter: 'agSetColumnFilter',
        filterValueGetter: ({ data }) => getSetFilterValue(data, field),
        filterParams: {
            valueFormatter: ({ value }) => getValueLabel(value),
            comparator: compareFilterValues(order),
            cellRenderer: FilterValueWithCount,
        },
    };
};

const numberFilter = {
    filter: 'agTextColumnFilter',
    filterValueGetter: ({ data }) => data.digits,
    getQuickFilterText: ({ data }) => data.digits,
    filterParams: {
        filterOptions: NUMBER_FILTER_OPTIONS,
        defaultOption: NUMBER_FILTER_OPTIONS[0].displayKey,
        maxNumConditions: 1,
        // Keeps a custom regex exactly as typed
        caseSensitive: true,
    },
};

// Filters driven only by the toolbar, so never shown as columns
const hiddenFilterCol = (colId, filterProps) => ({
    colId,
    hide: true,
    lockVisible: true,
    suppressColumnsToolPanel: true,
    ...filterProps,
});

/**
 * The same columns serve every view, so filters on them carry across. A view
 * change only shows or hides FULL_VIEW_COLUMNS (they start hidden, for Split
 * View).
 */
export const getAllNumbersColumns = ({ countries, tenant }) => {
    const fullViewOnly = (col, key) => ({
        ...col,
        ...attributeFilter(key),
        initialHide: true,
    });
    return [
        { ...makeNumberCol(countries), ...numberFilter, lockVisible: true },
        fullViewOnly(makeCountryCol(), 'country'),
        // Shown in every view: it's why most greyed out numbers can't be ticked
        { ...makeUserCol(), ...attributeFilter('user') },
        { ...makeStatusCol(), ...attributeFilter('status') },
        fullViewOnly(makeTrunkCol(), 'trunk'),
        ...(tenant.oc
            ? [
                  fullViewOnly(makeCallingProfileCol(), 'callingProfile'),
                  fullViewOnly(makeUsageCol(), 'usage'),
              ]
            : []),
        hiddenFilterCol(FILTER_COLUMNS.overstampDiffers, {
            filter: 'agSetColumnFilter',
            filterValueGetter: ({ data }) =>
                isOverstampDifferent(data) ? OVERSTAMP_DIFFERS_VALUE : 'No',
        }),
        hiddenFilterCol(FILTER_COLUMNS.paste, {
            filter: 'agSetColumnFilter',
            filterValueGetter: ({ data }) => data.digits,
        }),
    ];
};

export const getSelectedNumbersColumns = ({
    view,
    countries,
    tenant,
    onRemove,
}) => [
    { ...makeNumberCol(countries, 'Selected Number'), lockVisible: true },
    makeStatusCol(),
    ...(view === VIEW_MODES.SELECTED ? getDetailCols(tenant) : []),
    makeRemoveCol(onRemove),
];

export const defaultColDef = {
    sortable: false,
    resizable: true,
    suppressHeaderMenuButton: true,
    suppressMovable: true,
    // Search matches the number only (the Number column overrides this)
    getQuickFilterText: () => '',
};

// Numbers with an unselectable reason (see getUnselectableReason) are shown
// greyed out and can't be ticked, by hand, Shift range, select all or paste.
export const allNumbersRowSelection = {
    mode: 'multiRow',
    checkboxes: true,
    headerCheckbox: true,
    selectAll: 'filtered',
    enableClickSelection: false,
    hideDisabledCheckboxes: false,
    isRowSelectable: (node) => !node.data?.unselectableReason,
};

export const getAllNumbersRowStyle = ({ data }) =>
    data?.unselectableReason ? { opacity: 0.5 } : undefined;
